import React, { useState, useCallback, useEffect, memo, useRef } from "react";
import { Input } from '@nextui-org/react';

// Memoize SVG components since they're static
const SearchIcon = memo((props: React.SVGProps<SVGSVGElement>) => (
    <svg
        aria-hidden='true'
        fill='none'
        focusable='false'
        height='1em'
        role='presentation'
        viewBox='0 0 24 24'
        width='1em'
        {...props}
    >
        <path
            d='M11.5 21C16.7467 21 21 16.7467 21 11.5C21 6.25329 16.7467 2 11.5 2C6.25329 2 2 6.25329 2 11.5C2 16.7467 6.25329 21 11.5 21Z'
            stroke='currentColor'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='2'
        />
        <path
            d='M22 22L20 20'
            stroke='currentColor'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='2'
        />
    </svg>
));

SearchIcon.displayName = 'SearchIcon';

const CloseFilledIcon = memo((props: React.SVGProps<SVGSVGElement>) => (
    <svg
        aria-hidden='true'
        focusable='false'
        height='1em'
        role='presentation'
        viewBox='0 0 24 24'
        width='1em'
        {...props}
    >
        <path
            d='M12 2a10 10 0 1010 10A10.016 10.016 0 0012 2zm3.36 12.3a.754.754 0 010 1.06.748.748 0 01-1.06 0l-2.3-2.3-2.3 2.3a.748.748 0 01-1.06 0 .754.754 0 010-1.06l2.3-2.3-2.3-2.3A.75.75 0 019.7 8.64l2.3 2.3 2.3-2.3a.75.75 0 011.06 1.06l-2.3 2.3z'
            fill='currentColor'
        />
    </svg>
));

CloseFilledIcon.displayName = 'CloseFilledIcon';

// Memoized input class names
const inputClassNames = {
    label: "!text-darkBlue dark:text-white/90 font-bold text-medium",
    input: "bg-transparent text-black/90 dark:text-white/90 text-bold placeholder:text-default-700/80 dark:placeholder:text-white/60",
    innerWrapper: "bg-transparent",
    inputWrapper: "bg-default-200/50 dark:bg-default/60 backdrop-blur-xl backdrop-saturate-200 hover:bg-default-200/70 rounded-sm dark:hover:bg-default/70 group-data-[focus=true]:bg-default-200/50 dark:group-data-[focus=true]:bg-default/60 !cursor-text",
    clearButton: "bg-lightOrange/30"
};

interface MyInputProps {
    onChangeValue: (value: string) => void;
    shouldDisable: boolean;
    searchDescription: string;
    onResetResult: boolean;
    value?: string;
}

// Create a debounce utility outside component to prevent recreation
const debounce = <T extends (...args: any[]) => void>(func: T, delay: number) => {
    let timer: NodeJS.Timeout | null = null;
    return (...args: Parameters<T>) => {
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => func(...args), delay);
    };
};

const MyInput = memo(({ onChangeValue, shouldDisable, searchDescription, onResetResult, value }: MyInputProps) => {
    const [inputValue, setInputValue] = useState<string>(value || "");
    const inputRef = useRef<HTMLInputElement>(null);

    // Sync with external value prop
    useEffect(() => {
        if (value !== undefined && value !== inputValue) {
            setInputValue(value);
        }
    }, [value]);

    // Reset input value when onResetResult changes
    useEffect(() => {
        if (onResetResult) {
            setInputValue('');
        }
    }, [onResetResult]);

    // Memoize the debounced update function
    const debouncedUpdate = useCallback(
        debounce((value: string) => {
            onChangeValue(value);
        }, 500),
        [onChangeValue]
    );

    // Memoize the input change handler
    const handleInputChange = useCallback((value: string) => {
        setInputValue(value);
        debouncedUpdate(value);
        // Simplified focus management
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, [debouncedUpdate]);

    // Memoize the clear handler
    const handleClear = useCallback(() => {
        setInputValue('');
        debouncedUpdate('');
        // Focus the input after clearing
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, [debouncedUpdate]);

    return (
        <div className='bg-gradient-to-r from-darkBlue/50 to-lightOrange/50 text-white font-semibold p-1 w-full mb-5 rounded-lg'>
            <Input
                ref={inputRef}
                isClearable
                disabled={shouldDisable}
                className='flex w-full bg-lightGrey text-white text-2xl'
                classNames={inputClassNames}
                label='Search'
                placeholder={searchDescription}
                radius='lg'
                value={inputValue}
                onChange={(e) => handleInputChange(e.target.value)}
                startContent={
                    <SearchIcon className='text-black/50 mb-0.5 dark:text-white/90 text-slate-400 pointer-events-none flex-shrink-0'/>
                }
                onClear={handleClear}
                autoComplete='off'
            />
        </div>
    );
});

MyInput.displayName = 'MyInput';

export default MyInput;

